<script lang="ts">
import {
  OrSidebarV3,
  OrDrawerV3,
  OrExpansionPanelV3,
  OrSelectV3,
  OrMenuItemV3,
  OrTooltipV3,
  OrIconV3,
  OrPopoverV3,
  useResponsive,
} from '@onereach/ui-components';
import { useEventBus, createReusableTemplate } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';

import { useCheckPermission, useI18n } from '@/composables';
import { menuItems as allMenuItems } from '@/constants';
import { useCcpStore } from '@/stores';
const { t } = useI18n();

const [DefineNavigationItems, ReuseNavigationItems] = createReusableTemplate();

export default defineComponent({
  name: 'NavSidebar',
  components: {
    OrSidebarV3,
    OrDrawerV3,
    OrExpansionPanelV3,
    OrSelectV3,
    OrMenuItemV3,
    OrTooltipV3,
    OrIconV3,
    OrPopoverV3,
    DefineNavigationItems,
    ReuseNavigationItems,
  },
  props: {
    internalMode: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup() {
    const iconRef = ref<InstanceType<typeof OrIconV3>>(null!);
    const popoverRef = ref<InstanceType<typeof OrPopoverV3>>(null!);

    const ccpStore = useCcpStore();
    const { canAccessRoute } = useCheckPermission();

    const { customersList, selectedCustomer } = storeToRefs(ccpStore);

    const customersOptions = computed(() => customersList.value.map(customer => {
      return {
        label: customer.name,
        value: customer.id,
      };
    }));

    const { isMobile } = useResponsive();
    const isOpen = ref(false);

    const sidebarVariant = computed(() => isMobile.value ? 'OrDrawerV3' : 'OrSidebarV3');

    const menuItems = computed(() => {
      return allMenuItems.filter((item) => canAccessRoute(item.to));
    });

    const menuGroupItems = computed(() => {
      return [...new Set(menuItems.value.map(item => item.group))];
    });

    const filterMenuItemsByGroup = (group?: string) => {
      return menuItems.value.filter(item => group ? item.group === group : item.group === '');
    };

    const toggleSidebar = (value: boolean) => isOpen.value = value;

    watch(isMobile, (value) => {
      if (!value && isOpen.value) isOpen.value = false;
    });

    const { on, off } = useEventBus<boolean>('sidebar:open-drawer');

    onMounted(() => on(toggleSidebar));
    onBeforeUnmount(() => off(toggleSidebar));

    // Helper to resolve translation keys for group
    const groupLabels = computed(() => {
      const labels: Record<string, string> = {};
      menuGroupItems.value.forEach((group: string) => {
        // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
        labels[group] = group ? t(`sidebar.group.${group}`) : '';
      });
      return labels;
    });
    const menuItemLabels = computed(() => {
      const labels: Record<string, string> = {};
      menuItems.value.forEach((item) => {
        // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
        labels[item.label] = t(item.label);
      });
      return labels;
    });

    return {
      iconRef,
      popoverRef,
      isMobile,
      sidebarVariant,
      isOpen,
      selectedCustomer,
      customersOptions,
      menuGroupItems,
      groupLabels,
      menuItemLabels,
      toggleSidebar,
      filterMenuItemsByGroup,
    };
  },
});
</script>

<template>
  <DefineNavigationItems v-slot="{ isCollapsed }">
    <template v-if="internalMode">
      <OrSelectV3
        v-if="!isCollapsed"
        v-model="selectedCustomer"
        :options="customersOptions"
        :placeholder="$t('activity.SelectCustomer')"
        :label="$t('activity.SelectCustomer')"
        enable-search
        enable-clear
        :class="{ 'px-sm': isMobile }"
      />
      <OrTooltipV3
        v-else
        content="Select customer"
        placement="right"
        :debounce="0"
        class="flex w-full mt-[21px]"
      >
        <OrIconV3
          ref="iconRef"
          icon="person_search"
          variant="outlined"
          class="cursor-pointer w-full py-[19px] px-md hover:bg-primary-opacity-0-08 rounded-md hover:dark:bg-primary-opacity-0-08-dark"
          @click="popoverRef.open()"
        />
      </OrTooltipV3>
      <OrPopoverV3
        ref="popoverRef"
        placement="left"
        :trigger="iconRef?.root"
        variant="popover"
      >
        <div class="w-[295px] p-md">
          <OrSelectV3
            v-model="selectedCustomer"
            :options="customersOptions"
            :placeholder="$t('activity.SelectCustomer')"
            :label="$t('activity.SelectCustomer')"
            enable-search
            enable-clear
            :class="{ 'px-sm': isMobile }"
          />
        </div>
      </OrPopoverV3>
    </template>
    <div
      v-for="(group, idx) in menuGroupItems"
      :key="group"
    >
      <div
        v-if="!isCollapsed"
        class="typography-caption-regular text-outline dark:text-outline-dark px-lg py-sm md:px-sm"
      >
        {{ groupLabels[group] }}
      </div>
      <div
        v-else
        class="flex items-center typography-caption-regular text-outline dark:text-outline-dark py-sm before:content-['\200B']"
      >
        <hr
          v-show="internalMode || idx > 0"
          class="w-full border-t-1 border-outline-variant dark:border-outline-variant-dark"
        >
      </div>
      <!-- eslint-disable-next-line vue/no-undef-components -->
      <RouterLink
        v-for="menuItem in filterMenuItemsByGroup(group)"
        v-slot="{ isExactActive }"
        :key="menuItem.label"
        :to="menuItem.to"
      >
        <OrTooltipV3
          :content="isCollapsed ? menuItemLabels[menuItem.label] : ''"
          :placement="'right'"
          :debounce="0"
          class="flex w-full"
        >
          <OrMenuItemV3
            :class="[
              'w-full text-on-background dark:text-on-background-dark md:hover:rounded-md',
              isExactActive
                ? '!typography-body-2-semibold bg-primary-opacity-0-08 dark:bg-primary-opacity-0-08-dark md:rounded-md'
                : '!typography-body-2-regular',
              isCollapsed
                ? '!gap-0 before:content-[\'\\200B\']'
                : '',
            ]"
            :icon="{ icon: menuItem.icon, variant: isExactActive ? 'filled' : 'outlined' }"
            :selected="isExactActive"
            @click="toggleSidebar(false)"
          >
            <template v-if="!isCollapsed">
              {{ menuItemLabels[menuItem.label] }}
            </template>
          </OrMenuItemV3>
        </OrTooltipV3>
      </RouterLink>
    </div>
  </DefineNavigationItems>

  <component
    :is="sidebarVariant"
    class="w-full"
    placement="left"
    :width="255"
    collapsible
    :is-open="isMobile ? isOpen : true"
    :padding="isMobile ? false : undefined"
    data-test="admin-items-sidebar"
    @close="toggleSidebar(false)"
  >
    <div class="layout-column">
      <ReuseNavigationItems />
    </div>
    <template #collapsedContent>
      <div class="gap-sm">
        <ReuseNavigationItems :is-collapsed="true" />
      </div>
    </template>
  </component>
</template>
